import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IInventoryOption } from '../../../../Interfaces/IInventoryOption';
import { ILegalTransactionRelationshipRealEstate } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import {
  ILegalTransactionRelationshipRealEstateInventoryOption,
  initRealEstateInventoryOption,
  legalTransactionRelationshipRealEstateInventoryOptionSchema,
} from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstateInventoryOption';
import { useAlert } from '../../../../context/AlertContext';

interface IProps {
  realEstateId: number;
  inventoryOptions: IInventoryOption[];
  selectedInventoryOption?: ILegalTransactionRelationshipRealEstateInventoryOption;
  unmount: () => void;
  setFormValues: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationshipRealEstate>
  >;
}

export const CreateOrUpdateRealEstateInventoryOptionDialog: React.FC<
  IProps
> = ({
  inventoryOptions,
  selectedInventoryOption,
  realEstateId,
  unmount,
  setFormValues,
}) => {
  const [currentForm, setCurrentForm] =
    useState<ILegalTransactionRelationshipRealEstateInventoryOption>(
      initRealEstateInventoryOption(inventoryOptions[0], realEstateId)
    );

  useEffect(() => {
    if (!selectedInventoryOption) return;
    setCurrentForm(selectedInventoryOption);
  }, [selectedInventoryOption]);

  const { showAlert } = useAlert();

  const onSubmit = useCallback(() => {
    const { success, error, data } =
      legalTransactionRelationshipRealEstateInventoryOptionSchema.safeParse(
        currentForm
      );

    if (!success) {
      console.error(error);
      showAlert({
        text: 'Bitte füllen Sie alle Felder aus',
        severity: 'error',
      });
      return;
    }

    setFormValues(prev => ({
      ...prev,
      InventoryOptionArray: !selectedInventoryOption
        ? [...prev.InventoryOptionArray, data]
        : [
            ...prev.InventoryOptionArray.map(x =>
              x.idInventoryOption === data.idInventoryOption ? data : x
            ),
          ],
    }));
    unmount();
  }, [setFormValues, currentForm]);

  const isTextFieldEnabled = useMemo(
    () =>
      !!inventoryOptions.find(
        o => o.idInventoryOption === currentForm.idInventoryOption
      )?.enableTextField,
    [currentForm.idInventoryOption, inventoryOptions]
  );

  return (
    <Dialog open={true} onClose={unmount} maxWidth='sm' fullWidth>
      <DialogTitle>
        {selectedInventoryOption ? 'Anlage anpassen' : 'Anlage hinzufügen'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }} />
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <FormControl fullWidth required>
              <InputLabel>Anlage</InputLabel>
              <Select
                label='Anlage'
                size='small'
                fullWidth
                value={currentForm.idInventoryOption}
                onChange={({ target: { value } }) => {
                  const inventoryOptionId = Number(value);
                  const option = inventoryOptions.find(
                    x => x.idInventoryOption === inventoryOptionId
                  )!;

                  setCurrentForm(x => ({
                    ...x,
                    idInventoryOption: inventoryOptionId,
                    Title: option.InventoryOption,
                    Amount: 1,
                    Description: null,
                  }));
                }}
              >
                {inventoryOptions.map((x, i) => (
                  <MenuItem key={i} value={x.idInventoryOption}>
                    {x.InventoryOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item sm={12}>
            <Collapse in={isTextFieldEnabled}>
              <TextField
                required
                label='Bezeichnung'
                size='small'
                fullWidth
                value={currentForm.Title}
                error={currentForm.Title === ''}
                onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentForm(x => ({
                    ...x,
                    Title: target.value,
                  }))
                }
              />
            </Collapse>
          </Grid>

          <Grid item sm={12}>
            <TextField
              label='Anzahl'
              size='small'
              type='number'
              fullWidth
              value={currentForm.Amount}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentForm(x => ({
                  ...x,
                  Amount: Math.max(1, Number(target.value) || 1),
                }))
              }
            />
          </Grid>
          <Grid item sm={12}>
            <TextField
              label='Beschreibung'
              size='small'
              fullWidth
              multiline
              rows={3}
              value={currentForm.Description ?? ''}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentForm(x => ({
                  ...x,
                  Description: target.value,
                }))
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={unmount}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          disabled={currentForm.idInventoryOption < 0}
          onClick={onSubmit}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
